import { Cloud, K8sProvider } from '@cast/shared-types';
import { CommitmentCloud, CommitmentType } from '@cast/types-pricing';

import { CommitmentTab } from './_types';
import { CommitmentCloudService, CommitmentProvider } from './types';

export const AUTO_ENABLE_SUPPORTED_PROVIDERS: CommitmentCloud[] = [
  'AWS',
  'GCP',
  'AZURE',
];

// Note: order determines tab order in the UI
export const PROVIDER_TO_COMMITMENT_TABS: Record<
  CommitmentProvider,
  CommitmentTab[]
> = {
  [K8sProvider.AKS]: [CommitmentTab.AZURE_RI, CommitmentTab.AZURE_SP],
  [K8sProvider.GKE]: [
    CommitmentTab.GCP_RESOURCE_CUD,
    CommitmentTab.GCP_FLEX_CUD,
    CommitmentTab.GCP_RESERVATION,
  ],
  [K8sProvider.EKS]: [
    CommitmentTab.AWS_RI,
    CommitmentTab.AWS_SP,
    CommitmentTab.AWS_CAPACITY_RESERVATION,
  ],
};

export const COMMITMENT_TAB_TO_CLOUD_SERVICES = {
  [CommitmentTab.AZURE_RI]: CommitmentCloudService.AZURE_RI,
  [CommitmentTab.AZURE_SP]: CommitmentCloudService.AZURE_SP,
  [CommitmentTab.GCP_RESOURCE_CUD]: CommitmentCloudService.GCP_RESOURCE_CUD,
  [CommitmentTab.GCP_FLEX_CUD]: CommitmentCloudService.GCP_FLEX_CUD,
  [CommitmentTab.AWS_RI]: CommitmentCloudService.AWS_RI,
  [CommitmentTab.AWS_SP]: CommitmentCloudService.AWS_SP,
  [CommitmentTab.AWS_CAPACITY_RESERVATION]: [
    CommitmentCloudService.AWS_ODCR,
    CommitmentCloudService.AWS_CAPACITY_BLOCK,
  ],
  [CommitmentTab.GCP_RESERVATION]: CommitmentCloudService.GCP_RESERVATION,
} as const satisfies Record<
  CommitmentTab,
  CommitmentCloudService[] | CommitmentCloudService
>;

export const COMMITMENT_TAB_TO_API_PARAMS: Record<
  CommitmentTab,
  { cloud: CommitmentCloud; commitmentTypes: CommitmentType[] }
> = {
  [CommitmentTab.AZURE_RI]: {
    cloud: 'AZURE',
    commitmentTypes: ['RESERVED_INSTANCE'],
  },
  [CommitmentTab.AZURE_SP]: {
    cloud: 'AZURE',
    commitmentTypes: ['SAVINGS_PLAN'],
  },
  [CommitmentTab.AWS_RI]: {
    cloud: 'AWS',
    commitmentTypes: ['RESERVED_INSTANCE'],
  },
  [CommitmentTab.AWS_SP]: {
    cloud: 'AWS',
    commitmentTypes: ['SAVINGS_PLAN'],
  },
  [CommitmentTab.GCP_RESOURCE_CUD]: {
    cloud: 'GCP',
    commitmentTypes: ['RESOURCE_CUD'],
  },
  [CommitmentTab.GCP_FLEX_CUD]: {
    cloud: 'GCP',
    commitmentTypes: ['FLEX_CUD'],
  },
  [CommitmentTab.AWS_CAPACITY_RESERVATION]: {
    cloud: 'AWS',
    commitmentTypes: ['ON_DEMAND_CAPACITY_RESERVATION', 'CAPACITY_BLOCK'],
  },
  [CommitmentTab.GCP_RESERVATION]: {
    cloud: 'GCP',
    commitmentTypes: ['ON_DEMAND_CAPACITY_RESERVATION'],
  },
};

export const COMMITMENT_TAB_TO_LABEL: Record<CommitmentTab, string> = {
  [CommitmentTab.AZURE_RI]: 'RIs',
  [CommitmentTab.AZURE_SP]: 'SPs',
  [CommitmentTab.GCP_RESOURCE_CUD]: 'Resource CUDs',
  [CommitmentTab.AWS_RI]: 'RIs',
  [CommitmentTab.AWS_SP]: 'SPs',
  [CommitmentTab.GCP_FLEX_CUD]: 'Flexible CUDs',
  [CommitmentTab.AWS_CAPACITY_RESERVATION]: 'Capacity reservations',
  [CommitmentTab.GCP_RESERVATION]: 'Reservations',
};

export const COMMITMENT_TAB_TO_CLOUD: Record<CommitmentTab, Cloud> = {
  [CommitmentTab.AZURE_RI]: Cloud.azure,
  [CommitmentTab.AZURE_SP]: Cloud.azure,
  [CommitmentTab.GCP_RESOURCE_CUD]: Cloud.gcp,
  [CommitmentTab.GCP_FLEX_CUD]: Cloud.gcp,
  [CommitmentTab.AWS_RI]: Cloud.aws,
  [CommitmentTab.AWS_SP]: Cloud.aws,
  [CommitmentTab.AWS_CAPACITY_RESERVATION]: Cloud.aws,
  [CommitmentTab.GCP_RESERVATION]: Cloud.gcp,
};

export const COMMITMENT_TAB_TO_PROVIDER: Record<
  CommitmentTab,
  CommitmentProvider
> = {
  [CommitmentTab.AZURE_RI]: K8sProvider.AKS,
  [CommitmentTab.AZURE_SP]: K8sProvider.AKS,
  [CommitmentTab.GCP_RESOURCE_CUD]: K8sProvider.GKE,
  [CommitmentTab.GCP_FLEX_CUD]: K8sProvider.GKE,
  [CommitmentTab.AWS_RI]: K8sProvider.EKS,
  [CommitmentTab.AWS_SP]: K8sProvider.EKS,
  [CommitmentTab.AWS_CAPACITY_RESERVATION]: K8sProvider.EKS,
  [CommitmentTab.GCP_RESERVATION]: K8sProvider.GKE,
};

export const CLOUD_SERVICE_TO_PROVIDER: Record<
  CommitmentCloudService,
  CommitmentProvider
> = {
  [CommitmentCloudService.AZURE_RI]: K8sProvider.AKS,
  [CommitmentCloudService.AZURE_SP]: K8sProvider.AKS,
  [CommitmentCloudService.GCP_RESOURCE_CUD]: K8sProvider.GKE,
  [CommitmentCloudService.GCP_FLEX_CUD]: K8sProvider.GKE,
  [CommitmentCloudService.AWS_RI]: K8sProvider.EKS,
  [CommitmentCloudService.AWS_SP]: K8sProvider.EKS,
  [CommitmentCloudService.AWS_ODCR]: K8sProvider.EKS,
  [CommitmentCloudService.AWS_CAPACITY_BLOCK]: K8sProvider.EKS,
  [CommitmentCloudService.GCP_RESERVATION]: K8sProvider.GKE,
};
